import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Play, CheckCircle2, Clock, Trophy, Sparkles, ChevronRight, ArrowLeft, Lock, Target } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const CATEGORY_COLORS = {
  social: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: '#10b981' },
  career: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', accent: '#3b82f6' },
  emotional: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', accent: '#f43f5e' },
  confidence: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: '#f59e0b' },
};

const DIFFICULTY_BADGE = {
  beginner: { label: 'Beginner', color: 'bg-green-100 text-green-700' },
  intermediate: { label: 'Intermediate', color: 'bg-yellow-100 text-yellow-700' },
  advanced: { label: 'Advanced', color: 'bg-red-100 text-red-700' },
};

const ICON_MAP = {
  school: <BookOpen className="w-6 h-6" />,
  briefcase: <Target className="w-6 h-6" />,
  heart: <Sparkles className="w-6 h-6" />,
  mic: <Play className="w-6 h-6" />,
  users: <Trophy className="w-6 h-6" />,
};

const QuestsPage = () => {
  const navigate = useNavigate();
  const [quests, setQuests] = useState([]);
  const [activeQuests, setActiveQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('available');
  const [starting, setStarting] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [completionResult, setCompletionResult] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [availRes, activeRes] = await Promise.all([
        apiClient.request('/api/quests/available'),
        apiClient.request('/api/quests/active'),
      ]);
      setQuests(availRes.quests || []);
      setActiveQuests(activeRes.active_quests || []);
    } catch (e) {
      console.error('Failed to load quests', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const startQuest = async (questId) => {
    setStarting(questId);
    try {
      await apiClient.request('/api/quests/start', { method: 'POST', body: JSON.stringify({ quest_id: questId }) });
      await loadData();
      setTab('active');
    } catch (e) {
      alert(e.message || 'Failed to start quest');
    } finally {
      setStarting(null);
    }
  };

  const completeChapter = async (questId, chapter) => {
    setCompleting(`${questId}-${chapter}`);
    try {
      const res = await apiClient.request('/api/quests/complete-chapter', {
        method: 'POST',
        body: JSON.stringify({ quest_id: questId, chapter }),
      });
      setCompletionResult(res);
      await loadData();
      if (res.quest_completed) {
        setTimeout(() => setCompletionResult(null), 5000);
      } else {
        setTimeout(() => setCompletionResult(null), 3000);
      }
    } catch (e) {
      alert(e.message || 'Failed to complete chapter');
    } finally {
      setCompleting(null);
    }
  };

  const available = quests.filter(q => q.status === 'available');
  const inProgress = quests.filter(q => q.status === 'active');
  const completed = quests.filter(q => q.status === 'completed');

  return (
    <DashboardLayout>
      <div data-testid="quests-page" className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Social Quests</h1>
          <p className="text-base text-gray-500">Multi-session story arcs that build real social skills. Complete chapters, earn Hearts and exclusive NFTs.</p>
        </div>

        {/* Quest completion celebration */}
        {completionResult && (
          <div data-testid="quest-completion-alert" className={`mb-6 p-5 rounded-2xl border ${completionResult.quest_completed ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-start gap-3">
              {completionResult.quest_completed ? <Trophy className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />}
              <div>
                <p className="font-bold text-gray-900">{completionResult.message}</p>
                {completionResult.rewards && (
                  <p className="text-sm text-gray-600 mt-1">+{completionResult.rewards.hearts} Hearts | {completionResult.rewards.nft_rarity} NFT: {completionResult.rewards.nft_title}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6">
          {[
            { key: 'available', label: 'Available', count: available.length },
            { key: 'active', label: 'In Progress', count: inProgress.length },
            { key: 'completed', label: 'Completed', count: completed.length },
          ].map(t => (
            <button
              key={t.key}
              data-testid={`quest-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t.label} {t.count > 0 && <span className="ml-1 text-xs opacity-60">({t.count})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* Available quests */}
            {tab === 'available' && (
              <div className="space-y-4">
                {available.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-base font-medium">All quests started or completed!</p>
                  </div>
                )}
                {available.map(quest => {
                  const cat = CATEGORY_COLORS[quest.category] || CATEGORY_COLORS.social;
                  const diff = DIFFICULTY_BADGE[quest.difficulty] || DIFFICULTY_BADGE.beginner;
                  return (
                    <div key={quest.quest_id} data-testid={`quest-card-${quest.quest_id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-colors">
                      <div className="p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${cat.bg} ${cat.text}`}>
                            {ICON_MAP[quest.icon] || <BookOpen className="w-6 h-6" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900">{quest.title}</h3>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${diff.color}`}>{diff.label}</span>
                            </div>
                            <p className="text-sm text-gray-500 mb-3">{quest.description}</p>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {quest.total_chapters} chapters</span>
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> ~{quest.estimated_minutes}min</span>
                              <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {quest.rewards.hearts} Hearts + {quest.rewards.nft_rarity} NFT</span>
                            </div>
                          </div>
                          <button
                            data-testid={`start-quest-${quest.quest_id}`}
                            onClick={() => startQuest(quest.quest_id)}
                            disabled={starting === quest.quest_id}
                            className="w-full sm:w-auto px-5 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 flex-shrink-0"
                          >
                            {starting === quest.quest_id ? 'Starting...' : 'Begin Quest'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Active quests */}
            {tab === 'active' && (
              <div className="space-y-4">
                {activeQuests.length === 0 && inProgress.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <Play className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-base font-medium">No active quests</p>
                    <p className="text-sm mt-1">Start a quest from the Available tab!</p>
                  </div>
                )}
                {activeQuests.map(quest => {
                  const cat = CATEGORY_COLORS[quest.category] || CATEGORY_COLORS.social;
                  const progress = Math.round((quest.chapters_completed / quest.total_chapters) * 100);
                  const currentCh = quest.current_chapter;
                  return (
                    <div key={quest.quest_id} data-testid={`active-quest-${quest.quest_id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cat.bg} ${cat.text}`}>
                            {ICON_MAP[quest.icon] || <BookOpen className="w-5 h-5" />}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900">{quest.title}</h3>
                            <p className="text-xs text-gray-400">{quest.chapters_completed}/{quest.total_chapters} chapters</p>
                          </div>
                          <span className="text-sm font-bold" style={{ color: cat.accent }}>{progress}%</span>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: cat.accent }} />
                        </div>

                        {/* Current chapter */}
                        {currentCh && (
                          <div className="bg-gray-50 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-gray-400 uppercase">Chapter {currentCh.chapter}</span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">{currentCh.focus?.replace(/_/g, ' ')}</span>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1">{currentCh.title}</h4>
                            <p className="text-sm text-gray-500 mb-3">{currentCh.prompt}</p>
                            <div className="flex gap-2">
                              <button
                                data-testid={`practice-chapter-${quest.quest_id}`}
                                onClick={() => navigate('/conversations')}
                                className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
                              >
                                Practice This
                              </button>
                              <button
                                data-testid={`complete-chapter-${quest.quest_id}`}
                                onClick={() => completeChapter(quest.quest_id, currentCh.chapter)}
                                disabled={completing === `${quest.quest_id}-${currentCh.chapter}`}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                              >
                                {completing === `${quest.quest_id}-${currentCh.chapter}` ? 'Completing...' : 'Mark Complete'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Completed quests */}
            {tab === 'completed' && (
              <div className="space-y-4">
                {completed.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-base font-medium">No completed quests yet</p>
                    <p className="text-sm mt-1">Start your first quest to begin!</p>
                  </div>
                )}
                {completed.map(quest => (
                  <div key={quest.quest_id} data-testid={`completed-quest-${quest.quest_id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{quest.title}</h3>
                        <p className="text-xs text-gray-400">{quest.total_chapters}/{quest.total_chapters} chapters completed</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-600">+{quest.rewards?.hearts || 0} Hearts</p>
                        <p className="text-xs text-gray-400">{quest.rewards?.nft_rarity} NFT earned</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default QuestsPage;
